import React, { useEffect, useRef, useState } from "react"
// import { useRouteMatch, useHistory } from "react-router-dom"
import styles from "./index.module.scss"
import useQuery from "@/hooks/useQuery"
import { reportData } from "../../../utils/report-data"
import {
  ErrorPlaceholder,
  EmptyPlaceholder,
} from "../../../components/placeholder"
import LoadingPage from "@/components/loading-page"
import MessageBox from "@/components/message-box"
import {
  getSingleDayEnergyData,
  getSingleDayDriveSectionList
} from '@/api/energy-report'
import {
  formatTimestampToTxt
} from "@/utils/common"
import {
  calcRatio,
  formatEnergyData,
} from '../utils'
import '../home/reset.scss'

interface DistributionData {
  type: string,
  thisCycleValue: any,
  energyRatio: number,
  rangeOfChange: number | string
}

const DEFAULT_ERROR_MSG = '服务器开小差啦'

const App = () => {
  const vin = useQuery().get('vin') || ''
  const date = useQuery().get('date')
  const isSuperErev = useQuery().get('isSuperErev') === 'true'

  const debounceTimer = useRef<any>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingError, setIsLoadingError] = useState(false)
  const [isEmptyPage, setIsEmptyPage] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const [energyData, setEnergyData] = useState<any>({
    totalDriveMileage: '',
    avgDriveSpeed: '',
    updateTime: 0,
    drivePowerDetailsByDays: {},
    energyUsageDetail: {}
  })
  const [driveSectionData, setDriveSectionData] = useState<any[]>([])

  // TOP5能耗分布数据[{ type, thisCycleValue, energyRatio }]
  const [distributionType, setDistributionType] = useState('drive') // drive | parking
  const driveEnergyDistributionData = useRef<DistributionData[]>([])
  const parkingEnergyDistributionData = useRef<DistributionData[]>([])

  useEffect(() => {
    const displayDate = date ? `(${date.split('-')[1]}.${date.split('-')[2]})` : ''
    document.title = `能耗数据${displayDate}`

    setIsLoading(true)
    Promise.all([getSingleDayData(), getDriveSectionData()])
      .then(responses => {
        console.log('responses', responses)
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false)
      })

    return componentWillUnmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const componentWillUnmount = () => {
    clearTimeout(debounceTimer.current)
  }

  const showMessageBox = (title: string, message: string, confirmButtonText = '知道了') => {
    MessageBox.alert(message, { title, confirmButtonText })
  }

  // const showEnergyRecoveryTips = () => {
  //   showMessageBox('能量回收', '“行程耗电”为行程中的实际耗电量，行程耗电+能量回收电量=行程总输出电量')
  // }

  const showOtherTips = (type: string) => {
    let title = ''
    let desc = ''
    if (type === 'drive') {
      title = '行程-其他'
      desc = '其他行驶期间的用电器，如车灯、大屏、音响等。'
    }
    if (type === 'parking') {
      title = '停车-其他'
      // desc = '非行驶状态时车辆各控制器的基础耗电，包括车辆上电到行驶前的基础耗电，以及锁车下电后的基础耗电。'
      desc = '车辆从锁车下电到下次驶动期间，车辆各控制器和其他用电器的基础耗电。'
    }
    showMessageBox(title, desc)
  }

  const getSingleDayData = () => {
    return new Promise<any>((resolve, reject) => {
      const params = isSuperErev ? { vin, date, isSuperErev } : { vin, date } 
      getSingleDayEnergyData(params)
      .then(res => {
        if (res.code === 200) {
          setEnergyData(res.data)
          formatDistributionData(res.data)
        } else {
          setErrorMsg(DEFAULT_ERROR_MSG)
          setIsEmptyPage(true)
        }
        resolve('')
      }).catch(err => {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsLoadingError(true)
        reject(err)
      })
    })
  }

  const getDriveSectionData = () => {
    return new Promise<any>((resolve, reject) => {
      const params = isSuperErev ? { vin, date, isSuperErev } : { vin, date } 
      getSingleDayDriveSectionList(params)
      .then(res => {
        if (res.code === 200) {
          setDriveSectionData(formatDriveSectionData(res.data))
        } else {
          setErrorMsg(DEFAULT_ERROR_MSG)
          setIsEmptyPage(true)
        }
        resolve('')
      }).catch(err => {
        setErrorMsg(DEFAULT_ERROR_MSG)
        setIsLoadingError(true)
        reject(err)
      })
    })
  }

  // 处理行程数据中的时间
  const formatDriveSectionData = (data: any) => {
    if (data?.length) {
      data.forEach((item: any) => {
        item.timeRange = formatTimeData(item)
      })
    }
    return data
  }

  const formatTimeData = (data: any) => {
    if (!data.startTime || !data.endTime) {
      return '--'
    }
    const timeRangeSeconds = (Number(data.endTime) - Number(data.startTime)) / 1000
    // 小于60min：Xmin；大于60min：XXh XXmin
    if (timeRangeSeconds < 60 * 60) {
      return `${Math.ceil(timeRangeSeconds / 60)}min`
    } else {
      return `${Math.floor(timeRangeSeconds / (60 * 60))}h ${Math.floor((timeRangeSeconds % (60 * 60)) / 60)}min`
    }
  }

  const formatDistributionData = (resData) => {
    // 获取能耗分布top5数据
    const driveCycleData: any[] = []
    const parkingCycleData: any[] = []

    // 计算行车能耗分布数据
    resData.energyUsageDetail?.driveConsumptionThisCycle?.forEach(item => {
      driveCycleData.push({
        type: item.type,
        thisCycleValue: item.value,
        energyRatio: calcRatio(item.value, resData.energyUsageDetail?.driveTotalConsumption)
      })
    })
    // 计算停车能耗分布数据
    resData.energyUsageDetail?.parkingConsumptionThisCycle?.forEach(item => {
      parkingCycleData.push({
        type: item.type,
        thisCycleValue: item.value,
        energyRatio: calcRatio(item.value, resData.energyUsageDetail?.parkingTotalConsumption)
      })
    })

    driveEnergyDistributionData.current = driveCycleData
    parkingEnergyDistributionData.current = parkingCycleData

    // 单日行程页面若没有行驶数据，能耗分布默认展示停车数据，否则默认展示行驶数据
    if (!resData.totalDriveMileage) {
      setDistributionType('parking')
    } else {
      setDistributionType('drive')
    }
  }

  /**
   * 行程/停车类型点击事件，由于会做埋点上报，点击触发时做300s防抖不会频繁发送上报请求
   * @param type
   */
  const handleClickEnergyType = (type: string) => {
    setDistributionType(type)

    const TYPE_MAP = {
      'drive': '行程',
      'parking': '停车'
    }

    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      // 行车/停车切换点击埋点
      reportData({pageId: 'P11142', buttonId: 'B001', vin: vin, params: {
        option: TYPE_MAP[type]
      }})
    }, 300)
  }

  const reloadPage = () => {
    window.location.reload()
  }

  const showCycleEnergyTips = () => {
    const title = isSuperErev ? '平均行车电耗' : '平均行车能耗' 
    const messageConent = isSuperErev ? 
      '指车辆在车速>0期间的综合电耗，包括电驱系统、空调、各类用电器等，按行程的总里程统计。' : 
      '指车辆在车速>0期间的综合能耗，包括电驱系统、空调、各类用电器等。'
    showMessageBox(title, messageConent)
  }

  const showCycleFuelTips = () => {
    showMessageBox('平均行车油耗', '指车辆在增程器启动期间的综合油耗，包括电池充电、电驱系统、空调、各类用电器等，按行程的总里程统计。')
  }

  const showEnergyDistributionTips = () => {
    const tripConsumptionText = isSuperErev ? '2. 行程耗电=行程总输出电量（含燃油发电量）-能量回收电量。' : '2. 行程耗电=行程总输出电量-能量回收电量。'
    const message  =  <><div>1. 行程耗电指车辆从开始驶动到锁车下电整个行程的耗电量。</div><div>{tripConsumptionText}</div><div>3. 停车耗电指车辆从锁车下电到下次驶动整个过程的耗电量。</div></>
    const title = '能耗分布'
    const confirmButtonText = '知道了'
    const className = 'energy-distribution-tips'
    MessageBox.alert(message, {title, confirmButtonText, className})
  }

  if (isLoading)
    return (
      <LoadingPage />
    )

  if (isLoadingError)
    return (
      <ErrorPlaceholder
        message={errorMsg}
        getData={reloadPage}
      ></ErrorPlaceholder>
    )

  if (isEmptyPage)
    return (
      <EmptyPlaceholder text={errorMsg}></EmptyPlaceholder>
    )

  const {
    totalDriveMileage,
    avgDriveSpeed,
    updateTime
  } = energyData
  // 解构第二层对象可能取到undefined导致抛异常白屏，这里用可选链操作符安全获取各数据
  const avgPowerOfThisCycle = energyData?.drivePowerDetailsByDays?.avgPowerOfThisCycle
  const avgFuelOfThisCycle = energyData?.drivePowerDetailsByDays?.avgFuelOfThisCycle
  const driveTotalConsumption = energyData?.energyUsageDetail?.driveTotalConsumption
  const parkingTotalConsumption = energyData?.energyUsageDetail?.parkingTotalConsumption
  const totalConsumption = energyData?.energyUsageDetail?.totalConsumption
  const recycledEnergy = energyData?.energyUsageDetail?.recycledEnergy

  const distributionData = distributionType === 'drive' ? driveEnergyDistributionData.current : parkingEnergyDistributionData.current

  return (
    <div className={styles.homePage}>
      <div className={styles.comprehensiveData}>
        <div className={styles.title}>综合数据</div>
        {/* <div className={styles.avgPower}>
          <div className={styles.num}>{formatEnergyData(avgPowerOfThisCycle)}</div>
          <div className={styles.dataName} onClick={showCycleEnergyTips}>
            平均行车能耗{'(kWh/100km)'}
            <div className={styles.helpIcon}></div>
          </div>
        </div> */}
        <div className={styles.avgDataBlock}>
            <div className={styles.avgPower}>
              <div className={styles.num}>{formatEnergyData(avgPowerOfThisCycle)}</div>
              <div className={styles.dataName} onClick={showCycleEnergyTips}>
                {isSuperErev ? '平均电耗': '平均行车能耗'}{'(kWh/100km)'}
                <div className={styles.helpIcon}></div>
              </div>
            </div>
          {
            isSuperErev && 
                <div className={styles.avgPower}>
                  <div className={styles.num}>{formatEnergyData(avgFuelOfThisCycle)}</div>
                  <div className={styles.dataName} onClick={showCycleFuelTips}>
                    平均油耗{'(L/100km)'}
                    <div className={styles.helpIcon}></div>
                  </div>
                </div>
          }
        </div>
        {
          isSuperErev ?
            <div className={styles.fuelDataContainer}>
              <div className={styles.dividingLine}></div>
              <div className={styles.driveDataContainer}>
                <div className={styles.driveData}>
                  <div className={styles.label}>行驶里程</div>
                  <div className={styles.data}>{formatEnergyData(totalDriveMileage)}km</div>
                </div>
                <div className={styles.driveData}>
                  <div className={styles.label}>平均车速</div>
                  <div className={styles.data}>{formatEnergyData(avgDriveSpeed)}km/h</div>
                </div>
              </div>
            </div> : 
            <div className={styles.dataRow}>
              <div className={styles.dataItem}>
                <div className={styles.num}>{formatEnergyData(totalDriveMileage)}</div>
                <div className={styles.dataName}>行驶里程{'(km)'}</div>
              </div>
              <div className={styles.dataItem}>
                <div className={styles.num}>{formatEnergyData(totalConsumption)}</div>
                <div className={styles.dataName}>总耗电量{'(kWh)'}</div>
              </div>
              <div className={styles.dataItem}>
                <div className={styles.num}>{formatEnergyData(avgDriveSpeed)}</div>
                <div className={styles.dataName}>平均车速{'(km/h)'}</div>
              </div>
            </div>
        }
      </div>
      <div className={styles.energyDistribution}>
        <div className={styles.title} onClick={showEnergyDistributionTips}>
          能耗分布
          <div className={styles.helpIcon}></div>
        </div>
        <div className={styles.dataRow}>
          <div className={styles.dataItem}>
            <div className={styles.num}>{formatEnergyData(driveTotalConsumption)}</div>
            <div className={styles.dataName}>行程耗电{'(kWh)'}</div>
          </div>
          <div className={styles.dataItem}>
            <div className={styles.num}>{formatEnergyData(parkingTotalConsumption)}</div>
            <div className={styles.dataName}>停车耗电{'(kWh)'}</div>
          </div>
        </div>
        <div className={styles.recovery}>
          <div className={styles.text}>
            已回收
            {/* <div className={styles.helpIcon}></div> */}
          </div>
          <div className={styles.data}>{formatEnergyData(recycledEnergy)}kWh</div>
        </div>
        <div className={styles.energyTypeBar}>
          <div className={`${styles.type} ${distributionType === 'drive' ? styles.active : ''}`} onClick={() => handleClickEnergyType('drive')}>行程</div>
          <div className={`${styles.type} ${distributionType === 'parking' ? styles.active : ''}`} onClick={() => handleClickEnergyType('parking')}>停车</div>
        </div>
        <div className={styles.dataDesc}>
          <span className={styles.energyType}>能耗类别</span>
          <span className={styles.powerConsumption}>耗电量</span>
        </div>
        {
          distributionData.length > 0 ?
          distributionData.map((item, index) => (
            <div className={styles.energyDataBar} key={index}>
              <div className={styles.type}>
                {item.type}
                {index === distributionData.length - 1 ? <div className={styles.helpIcon} onClick={() => showOtherTips(distributionType)}></div> : null}
              </div>
              <div className={styles.dataInfo}>
                <div className={styles.percentBar}>
                  <div className={styles.highlightBar} style={{width:`${item.energyRatio}%`}}></div>
                </div>
                <div className={styles.data}>{formatEnergyData(item.thisCycleValue)} kWh</div>
              </div>
            </div>
          )) : null
        }
      </div>
      <div className={styles.itineraryList}>
        <div className={styles.title}>
          行程列表
        </div>
        <div className={styles.tableHeader}>
          <div className={styles.time}>行车时间</div>
          <div className={styles.others}>
            <div className={styles.headerItem}>
              行车里程<br />
              <div className={styles.unit}>{'(km)'}</div>
            </div>
            {
              isSuperErev ? 
                <div className={styles.headerItem}>
                  平均油耗<br />
                  <div className={styles.unit}>{'(L/100km)'}</div>
                </div> :
                <div className={styles.headerItem}>
                  行车用时<br />
                  <div className={styles.unit}>{'(h, min)'}</div>
                </div>
            }
            {/* <div className={styles.headerItem}>
              行车用时<br />
              <div className={styles.unit}>{'(h, min)'}</div>
            </div> */}
            <div className={styles.headerItem}>
              平均电耗<br />
              <div className={styles.unit}>{'(kWh/100km)'}</div>
            </div>
          </div>
        </div>
        {
          driveSectionData.length > 0 ?
          driveSectionData.map((item, index) => (
            <div className={styles.tableRow} key={index}>
              <div className={styles.driveTime}>{formatTimestampToTxt(item.startTime, 'HH:mm')}-{formatTimestampToTxt(item.endTime, 'HH:mm')}</div>
              <div className={styles.otherData}>
                <span className={styles.mileage}>{formatEnergyData(item.driveMileage)}</span>
                {
                  isSuperErev ? 
                    <span className={styles.timeRange}>{formatEnergyData(item.avgDriveFuel)}</span> : 
                    <span className={styles.timeRange}>{item.timeRange}</span>
                }
                {/* <span className={styles.timeRange}>{item.timeRange}</span> */}
                <span className={styles.avgPower}>{formatEnergyData(item.avgDrivePower)}</span>
              </div>
            </div>
          )) :
          <div className={styles.tableRow}>
            <div className={styles.noData}>暂无数据</div>
          </div>
        }
      </div>
      <div className={styles.dataTips}>
        更新于：{formatTimestampToTxt(updateTime)}，每天凌晨更新前一日数据<br />
        数据源于云端，可能因车辆网络问题延迟上报，仅供参考
      </div>
    </div>
  )
}

export default App